import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import CustomStore from 'devextreme/data/custom_store';
import { Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { HhsgSharedEllipseSelectOption, HhsgSharedUserProfile } from '../../../models';
import { HhsgSharedConfigService, HhsgSharedLoadingService, HhsgSharedUserService } from '../../../services';
import { HhsgSharedClientAdminService } from '../../../services/client-admin.service';
import { HhsgSharedFormStateService } from '../../../services/form-state.service';
import { Constants } from '../../../utilities/constants.util';
import { ErrorUtil } from '../../../utilities/error.util';
import { Location } from '@angular/common';
import { RoleUtil } from '../../../utilities/role.util';
import { ClientAdvanceFilterStatus } from '../../../models/client-advance-filter-status';
import { DxDataGridComponent } from 'devextreme-angular';
import { HhsgSharedHeaderFilter } from '../../../models/header-filter.model';
import { HhsgSharedApplication } from '../../../models/application.model';

@Component({
  selector: 'hhsg-client-admin',
  templateUrl: './client-admin.component.html',
  styleUrls: ['./client-admin.component.scss']
})
export class HhsgSharedClientAdminComponent implements OnInit {
  @ViewChild('clientsGrid', { static: false }) clientsGrid: DxDataGridComponent;
  private applicationAbbreviation
  public clientsDataStore: CustomStore<any, any>;
  public filterByCurrentApplication = false;
  public showInactive = false;
  private sortedColumnNumber = 1;
  public statusList: { id: string; name: string; }[] = [];
  public user: HhsgSharedUserProfile;
  saveSuccessText: string[] = [];
  private applicationsForFilter: HhsgSharedApplication[] = [];    
  applicationAbbreviationFilter: HhsgSharedHeaderFilter[] = [];
  
  private _unsubscribe$ = new Subject<void>();
  @Input() filteredClientIdsSource: Observable<ClientAdvanceFilterStatus>;
  ellipsesMenuItems: HhsgSharedEllipseSelectOption[] = [];
  filteredClientIds: number[];
  private eventSubscription: Subscription;

  constructor(
    private _clientAdminService: HhsgSharedClientAdminService,
    private _configService: HhsgSharedConfigService,
    private _elementRef: ElementRef,
    private _formStateService: HhsgSharedFormStateService,
    public _router: Router,
    private _userService: HhsgSharedUserService,
    private _route: ActivatedRoute,
    private _location: Location,
    private _loadingService: HhsgSharedLoadingService) {
    this.applicationAbbreviation = this._configService.appSettings.applicationAbbreviation;
    this.filterByCurrentApplication = !this.isAppHap();
  }

  public ngOnDestroy(): void {
    this._formStateService.reset();
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
    this.eventSubscription?.unsubscribe();
  }

  public async ngOnInit(): Promise<void> {
    this._loadingService.setLoading(true);
    this._userService.getUserProfile()
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((user: HhsgSharedUserProfile) => {
        this.user = user;
        this._loadingService.setLoading(false);
      });
    this.applicationsForFilter = await this._clientAdminService.getAllApplications();

    try {
      this.reloadData();
    }
    catch (error) {
      ErrorUtil.logError(error);
    }

    this.createStatusList();
    this._route.queryParams.subscribe(params => {
      if (params && params.showSuccess && params.showSuccess === 'true') {
        this.saveSuccessText.push(Constants.SaveSuccess);
        this._location.replaceState('/admin/client');
      }
    });
    this.setEllipsesMenuItems();
    if (this.filteredClientIdsSource) {
      this.eventSubscription = this.filteredClientIdsSource.subscribe((clientAdvanceFilterStatus) => {
        if (clientAdvanceFilterStatus.filteredClientIds.length && clientAdvanceFilterStatus.isClientAdvanceFilterApplied) {
          this.filteredClientIds = clientAdvanceFilterStatus.filteredClientIds;
          this.reloadData();
        }
        else if (!clientAdvanceFilterStatus.filteredClientIds.length && clientAdvanceFilterStatus.isClientAdvanceFilterApplied) {
          this.clientsDataStore = null;
        }
        else if (!clientAdvanceFilterStatus.filteredClientIds.length && !clientAdvanceFilterStatus.isClientAdvanceFilterApplied) {
          this.filteredClientIds = [];
          this.reloadData();
        }
      });
    }
  }

  private changeSortedColumnBackgroundColor() {
    this.clearPreviousColumnColor();
    var dataRows = this._elementRef.nativeElement.getElementsByClassName("dx-row");
    for (let index = 0; index < dataRows.length; index++) {
      if (dataRows[index].children[this.sortedColumnNumber]) {
        dataRows[index].children[this.sortedColumnNumber].classList.add("cell-highlighted");
      }
    }
  }

  private clearPreviousColumnColor() {
    var dataRows = this._elementRef.nativeElement.getElementsByClassName("cell-highlighted");
    for (let index = 0; index < dataRows.length; index++) {
      dataRows[index].classList.remove("cell-highlighted");
    }
    if (dataRows.length > 0)
      dataRows[0].classList.remove("cell-highlighted");
  }

  private createStatusList(): void {
    this.statusList = [{
      id: Constants.Synced,
      name: Constants.Synced
    },
    {
      id: Constants.Failed,
      name: Constants.Failed
    }]
  }

  public isAppHap(): boolean {
    return this.applicationAbbreviation == Constants.HuronAcceleratorsAbbreviation;
  }

  public onAddClick() {
    this._router.navigate(['/', 'admin', 'client', 'edit', -1]);
  }

  public onClickEdit(clientId: number): void {
    this._router.navigate(['/', 'admin', 'client', 'edit', clientId]);
  }

  public onOptionChanged(events) {
    if (events.fullName.includes("sort")) {
      this.sortedColumnNumber = events.fullName.charAt(8);
    }
  }

  private reloadData(): void {
    this._loadingService.setLoading(true);
    this._clientAdminService.getItemList(this.showInactive, this.applicationAbbreviation, this.filterByCurrentApplication, this.filteredClientIds)
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(result => {
        this.clientsDataStore = result;
        this._loadingService.setLoading(false);
      });
  }

  public setTableHeaderStyle() {
    if (this.clientsGrid.instance.getDataSource() && this.applicationAbbreviationFilter.length < 1) {
      this.initializeHeaderFilter();
    }

   this.changeSortedColumnBackgroundColor();
  }

  public toggleFilterByCurrentApplication(isChecked: boolean) {
    this.filterByCurrentApplication = isChecked;
    this.reloadData();
  }

  public toggleShowInactive(isChecked: boolean) {
    this.showInactive = isChecked;
    this.reloadData();
  }
  public isHuronEmployee(): boolean {
    if (!this.user) {
      return false;
    }
    return RoleUtil.isHuronEmployee(this.user.roles);
  }

  setEllipsesMenuItems() {
    this.ellipsesMenuItems.push(new HhsgSharedEllipseSelectOption('Advanced Filters', true));
    this.ellipsesMenuItems.push(new HhsgSharedEllipseSelectOption('New Client', true));
    this.ellipsesMenuItems.push(new HhsgSharedEllipseSelectOption('Export', true));
  }

  public onEllipsisButtonClicked(selectedOption: HhsgSharedEllipseSelectOption): void {
    if (selectedOption.labelName == Constants.NewClient) {
      this._router.navigate(['/', 'admin', 'client', 'edit', -1]);
    }

    if (selectedOption.labelName.trim().toLowerCase() == Constants.AdvancedFilters.trim().toLowerCase()) {
      this._clientAdminService.announceClientAdvancedFiltersClicked(selectedOption);
    }
  }
  
  private initializeHeaderFilter(): void {
    let applications: string[] = [];
    
    this.applicationsForFilter.forEach(clientUser => {
      const application = clientUser.abbreviation?.split(",");
      applications.push(...application);
    });

    applications = [...new Set(applications.map(a => a.trim()))];
    
    this.applicationAbbreviationFilter = this.gridHeaderFilter(applications, 'applicationAbbreviations');
  } 
  
  private gridHeaderFilter(headerFilterNames: string[], headerFilterValue: string): HhsgSharedHeaderFilter[] {
	const headerFilters: HhsgSharedHeaderFilter[] = [];
	headerFilterNames.forEach(name => {
		const headerFilter: HhsgSharedHeaderFilter = {
			text: (name == '') ? '(Blanks)' : name,
			value: (name == '') ? '' : [headerFilterValue, 'contains', name]
		};
		headerFilters.push(headerFilter)
	});
	return headerFilters;
  }

}
